import type { FC, ReactNode } from 'react'
import type { HomepageData, ProductCardData } from '@/types'

import Link from 'next/link'
import { ArrowLeft, ArrowRight, Calculator, Layers, ShoppingBag } from 'lucide-react'
import { applyDiscount } from '@/lib/pricing'
import { useI18n } from '@/lib/i18n'
import { unitLabel } from '@/constants/units'
import { CmsText } from '@/components/store/cms-text'
import { Num, Price } from '@/components/store/price'
import { Reveal } from '@/components/store/reveal'

interface WholesaleBandProps {
    homepage: HomepageData
    sample: ProductCardData | null
}

export const WholesaleBand: FC<WholesaleBandProps> = ({ homepage, sample }): ReactNode => {
    const { tx, txn, locale, isRtl } = useI18n()
    const Arrow = isRtl ? ArrowLeft : ArrowRight

    const steps = [
        { icon: ShoppingBag, title: tx('أضف المنتجات بأي كمية', 'Add products in any quantity'), text: tx('اشترِ قطعة واحدة بسعر القطاعي أو كرتونة كاملة.', 'Buy a single piece at retail, or a full carton.') },
        { icon: Layers, title: tx('السعر يتغير تلقائياً', 'The price switches automatically'), text: tx('بمجرد الوصول لحد الجملة يُطبَّق سعر الجملة على كل القطع.', 'Reach the wholesale quantity and every piece gets the wholesale price.') },
        { icon: Calculator, title: tx('اعرف توفيرك قبل الطلب', 'See your savings first'), text: tx('السلة توضح الخصم والتوفير والإجمالي قبل التأكيد.', 'Your cart shows every discount, saving and the total before you confirm.') }
    ]

    const pricing = sample?.pricing
    const ladder: Array<{ label: ReactNode, price: number }> = []

    if (sample && pricing && pricing.wholesaleFinal !== null) {
        ladder.push({
            label: (
                <>
                    <Num value={1} />
                    {' '}
                    {unitLabel(sample.unit, locale)}
                </>
            ),
            price: pricing.retailFinal
        })
        ladder.push({
            label: (
                <>
                    <Num value={pricing.wholesaleMinQty} />
                    {' '}
                    {unitLabel(sample.unit, locale, pricing.wholesaleMinQty)}
                    {' '}
                    {tx('أو أكثر', 'or more')}
                </>
            ),
            price: pricing.wholesaleFinal
        })

        if (pricing.bulkOffer) {
            ladder.push({
                label: (
                    <>
                        <Num value={pricing.bulkOffer.minQty} />
                        {' '}
                        {unitLabel(sample.unit, locale, pricing.bulkOffer.minQty)}
                        {' '}
                        {tx('أو أكثر', 'or more')}
                    </>
                ),
                price: applyDiscount(pricing.bulkOffer, pricing.wholesaleFinal)
            })
        }
    }

    const top = ladder[0]?.price ?? 1

    return (
        <section id='wholesale' className='relative overflow-hidden bg-navy-950 text-white' aria-labelledby='wholesale-title'>
            <div className='absolute -end-32 top-0 size-[520px] rounded-full bg-cyan-500/15 blur-3xl' aria-hidden='true' />

            <div className='container-page relative grid gap-12 py-20 lg:grid-cols-2 lg:items-center lg:py-28'>
                <Reveal className='flex flex-col gap-8'>
                    <div className='flex flex-col gap-4'>
                        <span className='text-sm font-semibold text-cyan-300'>{tx('الجملة والقطاعي', 'Wholesale and retail')}</span>
                        <CmsText table='homepage_content' id={homepage.id} field={homepage.wholesaleTitle} as='h2' className='text-balance text-3xl font-bold leading-tight tracking-tight sm:text-4xl lg:text-5xl' />
                        {homepage.wholesaleText && (
                            <CmsText table='homepage_content' id={homepage.id} field={homepage.wholesaleText} as='p' className='max-w-xl text-pretty text-base leading-relaxed text-white/70 sm:text-lg' />
                        )}
                    </div>

                    <ol className='flex flex-col gap-4'>
                        {steps.map((step, index) => (
                            <li key={index} className='flex gap-4 rounded-2xl bg-white/[0.04] p-4 ring-1 ring-white/10'>
                                <span className='flex size-11 shrink-0 items-center justify-center rounded-2xl bg-cyan-400/15 text-cyan-300'>
                                    <step.icon className='size-5' aria-hidden='true' />
                                </span>
                                <div className='flex flex-col gap-1'>
                                    <span className='font-semibold'>{step.title}</span>
                                    <span className='text-sm leading-relaxed text-white/65'>{step.text}</span>
                                </div>
                            </li>
                        ))}
                    </ol>
                </Reveal>

                {sample && ladder.length > 1 && (
                    <Reveal delay={120} className='rounded-[2rem] bg-white p-6 text-navy-950 shadow-lift sm:p-8'>
                        <div className='flex flex-col gap-1'>
                            <span className='text-xs font-semibold text-cyan-700'>{tx('مثال حقيقي من المتجر', 'A real example from the store')}</span>
                            <CmsText table='products' id={sample.id} field={sample.name} as='h3' className='text-xl font-semibold leading-snug' />
                        </div>

                        <div className='mt-6 flex flex-col gap-4'>
                            {ladder.map((step, index) => {
                                const width = Math.max(28, Math.round((step.price / top) * 100))

                                return (
                                    <div key={index} className='flex flex-col gap-2'>
                                        <div className='flex items-center justify-between gap-3 text-sm'>
                                            <span className='text-navy-700'>{step.label}</span>
                                            <span className='flex items-baseline gap-1.5'>
                                                <Price value={step.price} className='text-lg font-bold' />
                                                <span className='text-xs text-muted-foreground'>
                                                    {'/ '}
                                                    {unitLabel(sample.unit, locale)}
                                                </span>
                                            </span>
                                        </div>
                                        <div className='h-3 overflow-hidden rounded-full bg-navy-50'>
                                            <div
                                                className={index === 0 ? 'h-full rounded-full bg-navy-300' : 'h-full rounded-full bg-gradient-to-r from-cyan-400 to-cyan-600'}
                                                style={{ width: `${width}%` }}
                                            />
                                        </div>
                                    </div>
                                )
                            })}
                        </div>

                        {pricing && pricing.wholesaleSavingPct > 0 && (
                            <p className='mt-6 rounded-2xl bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800 ring-1 ring-emerald-100'>
                                {tx('توفير يصل إلى', 'Save up to')}
                                {' '}
                                <span dir='ltr' className='font-bold'>{pricing.wholesaleSavingPct}%</span>
                                {' '}
                                {tx('على كل قطعة بسعر الجملة', 'on every piece at wholesale price')}
                            </p>
                        )}

                        <Link href={`/products/${sample.slug}`} className='group mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-navy-900 hover:text-cyan-700'>
                            {tx('جرّب الحاسبة على صفحة المنتج', 'Try the price calculator on the product page')}
                            <Arrow className='size-4 transition-transform group-hover:-translate-x-0.5 ltr:group-hover:translate-x-0.5' aria-hidden='true' />
                        </Link>
                    </Reveal>
                )}
            </div>
        </section>
    )
}
